import { useEffect } from 'react';
import { formatVN } from './ui';
import type { LotCalculatorState } from '../useLotCalculator';

export default function ExplanationSection({
  calc,
  open,
  onClose,
}: {
  calc: LotCalculatorState;
  open: boolean;
  onClose: () => void;
}) {
  const { balance, risk, riskAmount, lotSize, stopLoss, product, curr, hasError } = calc;

  // Close on Escape key + lock body scroll while open
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Kết quả của bạn"
        className="relative z-10 flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white"
      >
        {/* header */}
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5 md:px-8">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-600">
              <i className="ri-flashlight-line" />
              Kết quả của bạn
            </span>
            <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
              Khối lượng giao dịch đề xuất
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-slate-500">
              Hiểu rõ cách con số khối lượng được tạo ra từ các thông tin bạn nhập.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Đóng"
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-xl bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* body */}
        <div className="overflow-y-auto px-6 py-6 md:px-8">
          <p className="text-base leading-relaxed text-slate-600">
            {hasError ? (
              'Vui lòng nhập đầy đủ và hợp lệ các thông tin ở trên để xem giải thích kết quả.'
            ) : (
              <>
                Với số dư tài khoản{' '}
                <strong className="font-semibold text-slate-800">
                  {formatVN(balance, 0, 0)} {curr.label}
                </strong>{' '}
                và mức rủi ro{' '}
                <strong className="font-semibold text-slate-800">{formatVN(risk, 2, 2)}%</strong>, số tiền rủi ro cho giao
                dịch là{' '}
                <strong className="font-semibold text-amber-600">{formatVN(riskAmount, 0, 2)} {curr.label}</strong>. Với Stop
                Loss <strong className="font-semibold text-slate-800">{formatVN(stopLoss, 0, 0)} Pip</strong>, khối lượng giao
                dịch được tính dựa trên giá trị Pip của sản phẩm{' '}
                <strong className="font-semibold text-slate-800">{product.label}</strong> là{' '}
                <strong className="font-semibold text-amber-600">{formatVN(lotSize, 2, 2)} Lot</strong>.
              </>
            )}
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
            <StepCard
              index="01"
              title="Số dư tài khoản"
              value={hasError ? '—' : `${formatVN(balance, 0, 0)} ${curr.label}`}
              desc="Tổng vốn bạn sử dụng để giao dịch."
            />
            <StepCard
              index="02"
              title="Mức rủi ro"
              value={hasError ? '—' : `${formatVN(risk, 2, 2)}%`}
              desc={`Tương ứng ${formatVN(riskAmount, 0, 2)} ${curr.label} rủi ro.`}
            />
            <StepCard
              index="03"
              title="Khối lượng giao dịch"
              value={hasError ? '—' : `${formatVN(lotSize, 2, 2)} Lot`}
              desc="Kết quả đề xuất cho vị thế của bạn."
              highlight
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function StepCard({
  index,
  title,
  value,
  desc,
  highlight,
}: {
  index: string;
  title: string;
  value: string;
  desc: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`relative rounded-2xl border p-5 ${
        highlight ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200/80 bg-slate-50/60'
      }`}
    >
      <span className="font-mono text-xs font-bold text-slate-400">{index}</span>
      <p className="mt-2 text-sm font-semibold text-slate-700">{title}</p>
      <p className={`mt-1 font-mono text-xl font-bold tabular-nums ${highlight ? 'text-amber-600' : 'text-slate-800'}`}>
        {value}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-slate-400">{desc}</p>
    </div>
  );
}